import {
  PRINT_STORE_BRAND_KIT_QUERY,
  PRINT_STORE_CATEGORIES,
  PRINT_STORE_MULTIPLE_TEMPLATES_QUERY,
  PRINT_STORE_PREVIEW_IMAGE_URL,
  PRINT_STORE_SINGLE_TEMPLATE_QUERY
} from '@dtx-company/flow-codegen/src/code/queries'
import { printStoreApi } from './init'
import { sentenceCase } from '@dtx-company/true-common/src/utils/strings'
import type {
  Category,
  PrintStoreBrandKitQuery,
  PrintStoreBrandKitQueryVariables,
  PrintStoreCategoriesQuery,
  PrintStoreCategoriesQueryVariables,
  PrintStoreMultipleTemplatesQuery,
  PrintStoreMultipleTemplatesQueryVariables,
  PrintStorePreviewImageUrlQuery,
  PrintStorePreviewImageUrlQueryVariables,
  PrintStoreSingleTemplateQuery,
  PrintStoreSingleTemplateQueryVariables
} from '@dtx-company/flow-codegen/src/code/generated.types'

export const printStoreQueries = printStoreApi.injectEndpoints({
  endpoints: builder => ({
    printStoreTemplateDetails: builder.query<
      PrintStoreSingleTemplateQuery,
      PrintStoreSingleTemplateQueryVariables
    >({
      query: ({ templateId }) => ({
        document: PRINT_STORE_SINGLE_TEMPLATE_QUERY,
        variables: { templateId },
        authOnly: false
      }),
      providesTags: ['printStoreTemplateDetails']
    }),

    printStoreTemplates: builder.query<
      PrintStoreMultipleTemplatesQuery,
      PrintStoreMultipleTemplatesQueryVariables
    >({
      query: ({ productId, categoryIds, layouts, limit, after, fetchProducts }) => ({
        document: PRINT_STORE_MULTIPLE_TEMPLATES_QUERY,
        variables: { productId, categoryIds, layouts, limit, after, fetchProducts },
        authOnly: false
      }),
      providesTags: ['printStoreTemplates']
    }),

    printStoreBrandKit: builder.query<PrintStoreBrandKitQuery, PrintStoreBrandKitQueryVariables>({
      query: ({ ithacaId }) => ({
        document: PRINT_STORE_BRAND_KIT_QUERY,
        variables: { ithacaId },
        authOnly: true
      }),
      providesTags: ['printStoreBrandKit']
    }),

    printStorePreviewImageUrl: builder.query<
      PrintStorePreviewImageUrlQuery,
      PrintStorePreviewImageUrlQueryVariables
    >({
      query: ({ dhProjectId, format }) => ({
        document: PRINT_STORE_PREVIEW_IMAGE_URL,
        variables: { dhProjectId, format },
        authOnly: true
      }),
      providesTags: ['printStoreImageUrl']
    }),

    printStoreCategories: builder.query<
      PrintStoreCategoriesQuery,
      PrintStoreCategoriesQueryVariables
    >({
      query: ({ after, limit }) => ({
        document: PRINT_STORE_CATEGORIES,
        variables: { after, limit },
        authOnly: false
      }),
      transformResponse: (response: PrintStoreCategoriesQuery) => {
        const categories = response.getCategories?.categories || []
        const transformedCategories = categories.map((category: Category): Category => {
          category.categoryName = sentenceCase(category.categoryName)
          return category
        })
        if (response.getCategories) {
          response.getCategories.categories = transformedCategories
        }
        return response
      },
      providesTags: ['printStoreCategories']
    })
  })
})

export const {
  usePrintStoreTemplateDetailsQuery,
  usePrintStoreTemplatesQuery,
  usePrintStoreBrandKitQuery,
  usePrintStorePreviewImageUrlQuery,
  useLazyPrintStorePreviewImageUrlQuery,
  usePrintStoreCategoriesQuery
} = printStoreQueries
